import { CUSTOM_STOREY_IMAGE_PAPER_FORMAT, DEFAULT_CUSTOM_PAPER_WIDTH_CM } from '@constants'
import { Texture } from 'three'

const WHITE_PIXEL_THRESHOLD = 250

const A4_SIZE_METERS_LONG_EDGE = 0.297
const A3_SIZE_METERS_LONG_EDGE = 0.42
const A2_SIZE_METERS_LONG_EDGE = 0.594
const A1_SIZE_METERS_LONG_EDGE = 0.841
const A0_SIZE_METERS_LONG_EDGE = 1.189

const dinLongEdgeMeters = (paperFormat: string): number => {
  switch (paperFormat) {
    case 'A0':
      return A0_SIZE_METERS_LONG_EDGE
    case 'A1':
      return A1_SIZE_METERS_LONG_EDGE
    case 'A2':
      return A2_SIZE_METERS_LONG_EDGE
    case 'A3':
      return A3_SIZE_METERS_LONG_EDGE
    case 'A4':
      return A4_SIZE_METERS_LONG_EDGE
    default:
      return A1_SIZE_METERS_LONG_EDGE
  }
}

/**
 * Plane size in meters at scale 1:1.
 * DIN formats use the long edge; custom ("Andere") uses horizontal width in cm
 * with height derived from the image pixel aspect ratio.
 */
export const computeStoreyImagePlaneSize = ({
  imageWidthPx,
  imageHeightPx,
  paperFormat,
  paperWidthCm,
}: {
  imageWidthPx: number
  imageHeightPx: number
  paperFormat: string
  paperWidthCm?: number
}): { width: number; height: number } => {
  const aspectRatio = imageWidthPx / imageHeightPx

  if (paperFormat === CUSTOM_STOREY_IMAGE_PAPER_FORMAT) {
    const widthCm =
      paperWidthCm != null && Number.isFinite(paperWidthCm) && paperWidthCm > 0
        ? paperWidthCm
        : DEFAULT_CUSTOM_PAPER_WIDTH_CM
    const width = widthCm / 100
    return {
      width,
      height: width / aspectRatio,
    }
  }

  const longEdgeSize = dinLongEdgeMeters(paperFormat)

  if (imageWidthPx > imageHeightPx) {
    return {
      width: longEdgeSize,
      height: longEdgeSize / aspectRatio,
    }
  }

  return {
    width: longEdgeSize * aspectRatio,
    height: longEdgeSize,
  }
}

export const transparencyToOpacity = (transparency: number): number => (100 - transparency) / 100

const isWhitePixel = (r: number, g: number, b: number): boolean =>
  r >= WHITE_PIXEL_THRESHOLD && g >= WHITE_PIXEL_THRESHOLD && b >= WHITE_PIXEL_THRESHOLD

export const applyForegroundBackgroundTransparency = (
  sourceImage: HTMLImageElement | HTMLCanvasElement,
  sourceTexture: Texture,
  foregroundTransparency: number,
  backgroundTransparency: number,
): Texture => {
  const width = 'naturalWidth' in sourceImage ? sourceImage.naturalWidth : sourceImage.width
  const height = 'naturalHeight' in sourceImage ? sourceImage.naturalHeight : sourceImage.height

  if (!width || !height) return sourceTexture

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')
  if (!ctx) return sourceTexture

  ctx.drawImage(sourceImage, 0, 0, width, height)
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data
  const fgOpacity = transparencyToOpacity(foregroundTransparency)
  const bgOpacity = transparencyToOpacity(backgroundTransparency)

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i]
    const g = data[i + 1]
    const b = data[i + 2]
    const opacity = isWhitePixel(r, g, b) ? bgOpacity : fgOpacity
    data[i + 3] = Math.round(data[i + 3] * opacity)
  }

  ctx.putImageData(imageData, 0, 0)

  const processedTexture = new Texture(canvas)
  processedTexture.colorSpace = sourceTexture.colorSpace
  processedTexture.flipY = sourceTexture.flipY
  processedTexture.needsUpdate = true
  return processedTexture
}
